import React, { useEffect, useState } from 'react';
import { FilterList, KeyboardArrowDown, KeyboardArrowUp, Close, Add, DeleteForever } from '@mui/icons-material';
import { 
  Badge, 
  Button, 
  Popover, 
  Typography, 
  Box, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  TextField,
  IconButton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { FormProvider, useForm, Controller, useFieldArray } from 'react-hook-form';
import dayjs from 'dayjs';
import { CalendarIcon, DesktopDatePicker } from '@mui/x-date-pickers';
import useResponsive from '../../hooks/useResponsive';
import { normalizeNumericInput } from '../../utils/formatNumber';

const MultiColumnFilter = ({ 
  filterableColumns, 
  onApplyFilter, 
  externalClear = false, // to handle external clear
  defaultFilterCount = 1 ,
  defaultFilterOptions, // this is used in service expert list as pagination model is saved in local storage ...if filter options is present then only
  alwaysKeepDefaultFilterOption=false
}) => {
  const filterFormMethod = useForm({
    mode: "onChange",
    defaultValues: {
      filterOption: defaultFilterOptions?.length>0? defaultFilterOptions : Array(defaultFilterCount).fill().map(() => ({ 
        fieldName: '', 
        operator: 'contains', 
        value: null 
      }))
    }
  });

  const { control, handleSubmit, reset, setValue, getValues, watch } = filterFormMethod;
  const [anchorEl, setAnchorEl] = useState(null);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "filterOption"
  });

  // React to external clear prop
  useEffect(() => {
    if (externalClear) {
      reset({
        filterOption: [
          ...defaultFilterOptions
        ]
      });
    }
  }, [externalClear, reset]);

  const filterOption = watch('filterOption');

  const activeBadgeContent = filterOption.filter(
    row => row.fieldName && row.value
  ).length;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if(activeBadgeContent > 0){
      onApplyFilter(getValues());
    }
  };

  const openFilterForm = Boolean(anchorEl);

  const getAvailableColumns = (currentIndex) => {
    const selectedColumns = filterOption
      .filter((_, index) => index !== currentIndex)
      .map(option => option.fieldName)
      .filter(Boolean);

    return filterableColumns.filter(column => 
      !selectedColumns.includes(column.key)
    );
  };

  const handleColumnChange = (e, index, columnField) => {
    const selectedColumn = filterableColumns.find(col => col.key === e.target.value);
    
    // Reset the value
    setValue(`filterOption.${index}.value`, null);
    
    // Set default operator based on column type
    if (selectedColumn?.type === 'date') {
      setValue(`filterOption.${index}.operator`, 'equals');
    }else if(selectedColumn?.type === 'email'){
      setValue(`filterOption.${index}.operator`, 'equals');
    }
    else if(selectedColumn?.type === 'select'){
      setValue(`filterOption.${index}.operator`, 'equals');
    } 
    else if(selectedColumn?.type === 'number-range'){
      setValue(`filterOption.${index}.operator`, 'between');
    }
    // else if(selectedColumn?.type === 'multiselect'){
    //   setValue(`filterOption.${index}.operator`, 'in');
    // }
    else if(selectedColumn?.type === 'number'){
      setValue(`filterOption.${index}.operator`, 'equals');
    }
    else {
      setValue(`filterOption.${index}.operator`, 'contains');
    }
    
    // Call the original onChange handler
    columnField.onChange(e);
  };

  
  const renderValueField = (field, index,isDefault) => {
    const selectedColumn = filterableColumns.find(
      col => col.key === filterOption[index]?.fieldName
    );

    const currentOperator = filterOption[index]?.operator;
  
    // Handle different input types
    switch(selectedColumn?.type) {
      case 'date':
        // If operator is 'between', render two date pickers
        if (currentOperator === 'between') {
          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                <DesktopDatePicker
                  // disabled={!watch('services').length>0 || !watch('customer')}
                  
                  label="Start Date"
                  value={field.value? dayjs(field.value) : null}
                  onChange={(newValue) => {
                    field.onChange(newValue ? dayjs(newValue).endOf('day').toISOString() : null);
                  }}
                  format="DD-MM-YYYY" // Display format for user
                  // minDate={dayjs()}
                  slots={{
                    openPickerIcon: (props) => (
                      <CalendarIcon 
                        {...props} 
                        sx={{ 
                          fontSize: '1rem', // Control icon size
                          color: 'gray',    // Optional: control icon color
                          padding:0
                        }} 
                      />
                    ),
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      size: 'small',
                      variant: 'standard', // Use standard variant to match existing design
                      InputProps: {
                        // disableUnderline: true, // Remove underline if using standard variant
                        // inputProps: {
                          style: {
                            height: '1.6rem',  
                            fontSize: '0.75rem',                        
                          },
                        // },
                      },
                      InputLabelProps: {
                        style: {
                          fontSize: '0.75em'
                        }
                      }
                    }
                  }}
                />
                {/* <DatePicker
                  label="Start Date"
                  value={field.value}
                  onChange={(newValue) => {
                    field.onChange(newValue ? newValue.format("DD-MM-YYYY") : null);
                  }}
                  format="DD-MM-YYYY"
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      variant='standard' 
                      size='small'
                      InputProps={{
                        inputProps: {
                          style: {
                            height: '1.2rem',  
                            fontSize: '0.75rem',                        
                          },
                        },
                      }}  
                      InputLabelProps={{
                          style: {
                              fontSize: '0.75em'
                          }
                      }}  
                    />
                  )}
                /> */}
                <DesktopDatePicker
                  label="End Date"
                  value={filterOption[index]?.endValue ? dayjs(filterOption[index]?.endValue) : null}
                  onChange={(newValue) => {
                    setValue(`filterOption.${index}.endValue`, newValue ? dayjs(newValue).endOf('day').toISOString() : null);
                  }}
                  format="DD-MM-YYYY"
                  slots={{
                    openPickerIcon: (props) => (
                      <CalendarIcon 
                        {...props} 
                        sx={{ 
                          fontSize: '1rem', // Control icon size
                          color: 'gray',    // Optional: control icon color
                          padding:0
                        }} 
                      />
                    ),
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      size: 'small',
                      variant: 'standard', // Use standard variant to match existing design
                      InputProps: {
                        // disableUnderline: true, // Remove underline if using standard variant
                        // inputProps: {
                          style: {
                            height: '1.6rem',  
                            fontSize: '0.75rem',                        
                          },
                        // },
                      },
                      InputLabelProps: {
                        style: {
                          fontSize: '0.75em'
                        }
                      }
                    },            
                  }}
                  componentsProps={{
                    actionBar: {
                      actions: [], // Remove action buttons in calendar
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>
          );
        }
        
        // Default single date picker for other date operators
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              disabled={isDefault}
              label={selectedColumn.label}
              value={field.value ? dayjs(field.value) : null}
              onChange={(newValue) => {
                field.onChange(newValue? dayjs(newValue).endOf('day').toISOString(): null);
              }}
              format="DD-MM-YYYY"
              slots={{
                openPickerIcon: (props) => (
                  <CalendarIcon 
                    {...props} 
                    sx={{ 
                      fontSize: '1rem', // Control icon size
                      color: 'gray',    // Optional: control icon color
                      padding:0
                    }} 
                  />
                ),
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  size: 'small',
                  variant: 'standard', // Use standard variant to match existing design
                  InputProps: {
                    // disableUnderline: true, // Remove underline if using standard variant
                    // inputProps: {
                      style: {
                        height: '1.6rem',  
                        fontSize: '0.75rem',                        
                      },
                    // },
                  },
                  InputLabelProps: {
                    style: {
                      fontSize: '0.75em'
                    }
                  }
                }
              }}
            />
          </LocalizationProvider>
        );
      // case 'number-range':
      //   return (
      //     <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
      //       <TextField 
      //         // {...field} 
      //         value={filterOption[index]?.value}
      //         disabled={isDefault}
      //         type="number"
      //         inputMode="numeric"
      //         size='small'
      //         variant='standard'
      //         label='Start Value'
      //         InputProps={{
      //           inputProps: {
      //             style: {
      //               height: '1.2rem',     
      //               fontSize: '0.75rem',                     
      //             },
      //             min: 0,
      //             step: 1,
      //             onFocus: (e) => e.target.select(), // This will auto-select all text on focus/click
      //             onClick: (e) => e.target.select(),  // This ensures selection even on click when already focused 
      //             onWheel: (e) => e.target.blur(),  // Prevent scroll wheel
      //             onKeyDown: (e) => {
      //                 if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      //                     e.preventDefault();
      //                 }
      //             }
      //           },
      //         }}
      //         InputLabelProps={{
      //             style: {
      //                 fontSize: '0.75em'
      //             }
      //         }} 
      //         onChange={(e) => {
      //           const inputValue = e.target.value;
      //           let finalValue;
                
      //           // Adjust regex based on integer/float requirement
      //           const isInteger = true; // or false for float
      //           const pattern = isInteger ? /^\d*$/ : /^\d*\.?\d*$/;
                
      //           if (inputValue === '' || pattern.test(inputValue)) {
      //               finalValue = normalizeNumericInput(inputValue, isInteger ? 'integer' : 'float');
      //               console.log("finalValue", finalValue);

      //               if(finalValue === ''){
      //                   finalValue = 0;
      //               }

      //               field.onChange(finalValue);
      //           }
      //       }} 
      //       />
      //       {/* endValue should be greater than 0 and greater than value */}
      //       <TextField 
      //         // {...field} 
      //         value={filterOption[index]?.endValue}
      //         disabled={isDefault}
      //         type="number"
      //         inputMode="numeric"
      //         size='small'
      //         variant='standard'
      //         label='End Value'
      //         InputProps={{
      //           inputProps: {
      //             style: {
      //               height: '1.2rem',     
      //               fontSize: '0.75rem',                     
      //             },
      //             min: 0,
      //             step: 1,
      //             onFocus: (e) => e.target.select(), // This will auto-select all text on focus/click
      //             onClick: (e) => e.target.select(),  // This ensures selection even on click when already focused 
      //             onWheel: (e) => e.target.blur(),  // Prevent scroll wheel
      //             onKeyDown: (e) => {
      //                 if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      //                     e.preventDefault();
      //                 }
      //             }
      //           },
      //         }}
      //         InputLabelProps={{
      //             style: {
      //                 fontSize: '0.75em'
      //             }
      //         }} 
      //         onChange={(e) => {
      //           const inputValue = e.target.value;
      //           let finalValue;
                
      //           // Adjust regex based on integer/float requirement
      //           const isInteger = true; // or false for float
      //           const pattern = isInteger ? /^\d*$/ : /^\d*\.?\d*$/;
                
      //           if (inputValue === '' || pattern.test(inputValue)) {
      //               finalValue = normalizeNumericInput(inputValue, isInteger ? 'integer' : 'float');
      //               console.log("finalValue", finalValue);

      //               if(finalValue === ''){
      //                   finalValue = 0;
      //               }

      //               field.onChange(finalValue);
      //           }
      //       }} 
      //       />
      //     </Box>    
      // );
      
      case 'number-range':
        return (
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <TextField 
              value={filterOption[index]?.value || ''}
              disabled={isDefault}
              type="number"
              inputMode="numeric"
              size='small'
              variant='standard'
              label='Start Value'
              InputProps={{
                inputProps: {
                  style: {
                    height: '1.2rem',     
                    fontSize: '0.75rem',                     
                  },
                  min: 0,
                  step: 1,
                  onFocus: (e) => e.target.select(),
                  onClick: (e) => e.target.select(),
                  onWheel: (e) => e.target.blur(),
                  onKeyDown: (e) => {
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                          e.preventDefault();
                      }
                  }
                },
              }}
              InputLabelProps={{
                  style: {
                      fontSize: '0.75em'
                  }
              }} 
              onChange={(e) => {
                const inputValue = e.target.value;
                
                const isInteger = true;
                const pattern = isInteger ? /^\d*$/ : /^\d*\.?\d*$/;
                
                if (inputValue === '' || pattern.test(inputValue)) {
                    let finalValue = normalizeNumericInput(inputValue, isInteger ? 'integer' : 'float');
                    
                    if(finalValue === ''){
                        finalValue = 0;
                    }

                    setValue(`filterOption.${index}.value`, finalValue);
                }
              }} 
            />
            <TextField 
              value={filterOption[index]?.endValue || ''}
              disabled={isDefault}
              type="number"
              inputMode="numeric"
              size='small'
              variant='standard'
              label='End Value'
              error={filterOption[index]?.endValue && filterOption[index]?.value && Number(filterOption[index]?.endValue) <= Number(filterOption[index]?.value)}
              helperText={filterOption[index]?.endValue && filterOption[index]?.value && Number(filterOption[index]?.endValue) <= Number(filterOption[index]?.value) ? 'Must be > start value' : ''}
              InputProps={{
                inputProps: {
                  style: {
                    height: '1.2rem',     
                    fontSize: '0.75rem',                     
                  },
                  min: 0,
                  step: 1,
                  onFocus: (e) => e.target.select(),
                  onClick: (e) => e.target.select(),
                  onWheel: (e) => e.target.blur(),
                  onKeyDown: (e) => {
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                          e.preventDefault();
                      }
                  }
                },
              }}
              InputLabelProps={{
                  style: {
                      fontSize: '0.75em'
                  }
              }}
              FormHelperTextProps={{
                  style: {
                      fontSize: '0.65rem',
                      margin: '2px 0 0 0'
                  }
              }}
              onChange={(e) => {
                const inputValue = e.target.value;
                
                const isInteger = true;
                const pattern = isInteger ? /^\d*$/ : /^\d*\.?\d*$/;
                
                if (inputValue === '' || pattern.test(inputValue)) {
                    let finalValue = normalizeNumericInput(inputValue, isInteger ? 'integer' : 'float');
                    
                    if(finalValue === ''){
                        finalValue = 0;
                    }

                    setValue(`filterOption.${index}.endValue`, finalValue);
                }
              }} 
            />
          </Box>    
      );
      case 'select':
        return (
          <FormControl variant='standard' sx={{ width:'100%' }} size="small">
            <InputLabel id={`select-label-${index}`} sx={{fontSize:'.75em', lineHeight:'2em'}}>
              {selectedColumn.label}
            </InputLabel>
            <Select
              {...field}
              disabled={isDefault}
              labelId={`select-label-${index}`}
              value={field.value || ''}
              onChange={(e) => {
                field.onChange(e);
              }}
              sx={{
                minHeight: '0px',
                height: '1.6rem',
                fontSize: '0.75rem',
                fontWeight: '600',
                textTransform:'capitalize'
              }}
            >
              {selectedColumn.options.map(option => (
                <MenuItem key={option.value} value={option.value} sx={{fontSize:'0.75rem'}}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      
      case 'number':
        return (
          <TextField 
            {...field} 
            disabled={isDefault}
            type="number"
            size='small'
            variant='standard'
            label='Value'
            InputProps={{
              inputProps: {
                style: {
                  height: '1.2rem',     
                  fontSize: '0.75rem',                     
                },
              },
            }}
            InputLabelProps={{
                style: {
                    fontSize: '0.75em'
                }
            }}    
          />
        );
      
      case 'boolean':
        return (
          <FormControl variant='standard' sx={{ width:'100%' }} size="small">
            <InputLabel id={`boolean-label-${index}`} sx={{fontSize:'.75em', lineHeight:'2em'}}>
              {selectedColumn.label}
            </InputLabel>
            <Select
              {...field}
              disabled={isDefault}
              labelId={`boolean-label-${index}`}
              value={field.value || ''}
              onChange={(e) => {
                field.onChange(e);
              }}
              sx={{
                minHeight: '0px',
                height: '1.6rem',
                fontSize: '0.75rem',
                fontWeight: '600',
                textTransform:'capitalize'
              }}
            >
              <MenuItem value="true" sx={{fontSize:'.75rem'}}>Yes</MenuItem>
              <MenuItem value="false" sx={{fontSize:'.75rem'}}>No</MenuItem>
            </Select>
          </FormControl>
        );
      
      // Default to text input
      default:
        return (
          <TextField 
            {...field} 
            disabled={isDefault}
            size='small'
            variant='standard'
            label='Value'
            InputProps={{
              inputProps: {
                style: {
                  height: '1.2rem',  
                  fontSize: '0.75rem',                        
                },
              },
            }}  
            InputLabelProps={{
                style: {
                    fontSize: '0.75em'
                }
            }}  
          />
        );
    }
  };

  const getOperators = (columnType) => {
    switch(columnType) {
      case 'select':
        return [
          { value: 'equals', label: 'Is' },
        ];
      case 'email':
        return [
          // { value: 'contains', label: 'Contains' },
          { value: 'equals', label: 'Equals' },
        ];
      case 'number':
        return [
          { value: 'contains', label: 'Contains' },
          { value: 'equals', label: 'Is' },
          // { value: 'greaterThan', label: 'Greater Than' },
          // { value: 'lessThan', label: 'Less Than' }
        ];
      case 'number-range':
        return [
          { value: 'between', label: 'Between' }
        ];
      case 'date':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'lessThan', label: 'Before' },
          { value: 'greaterThan', label: 'After' },
          { value: 'between', label: 'Between' }
        ];
      case 'text':
      default:
        return [
          { value: 'contains', label: 'Contains' },
          // { value: 'equals', label: 'Equals' },
          // { value: 'startsWith', label: 'Starts With' },
          // { value: 'endsWith', label: 'Ends With' }
        ];
    }
  };


  // Modify onApplyFilter to handle date range
  const handleApplyFilter = (data) => {
    // Transform data to handle date range
    const transformedFilterOptions = data.filterOption.map(option => {
      if (option.fieldName && filterableColumns.find(col => col.key === option.fieldName)?.type === 'date') {
        if (option.operator === 'between') {
          return {
            ...option,
            value: option.value ? dayjs(option.value).toISOString() : null,
            endValue: option.endValue ? dayjs(option.endValue).toISOString() : null
          };
        }

        console.log("transformedFilterOptions:",{
          ...option,
          value: option.value ? dayjs(option.value).toISOString() : null
        });
        return {
          ...option,
          value: option.value ? dayjs(option.value).toISOString() : null
        };
      }
      return option;
    });

    onApplyFilter({ ...data, filterOption: transformedFilterOptions });
  };

  const clearFilters = () => {    
      reset();
      handleClose();
  };

 const {isXs, isSm,isLg,isMd,isXl} = useResponsive();
  return (
    <>
      {
        isSm?
        <IconButton
          onClick={handleClick}
          color="primary"
          sx={{
            p: 1, // padding around icon
          }}
        >
          <Badge
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.5rem',
                fontWeight: 600,
                minWidth: 0,
                height: 'auto',
                padding: '10% 17%',
                borderRadius: '50%',
              },
            }}
            badgeContent={activeBadgeContent}
            color="primary"
            invisible={activeBadgeContent === 0}
          >
            <FilterList />
          </Badge>
        </IconButton>

        :
        <Button
        onClick={handleClick}
        // endIcon={openFilterForm ? <KeyboardArrowUp/> : <KeyboardArrowDown />}
        sx={{
          textTransform: 'none',
          // fontSize:'x-small',
          // p:'3% 10%'
        }}
        startIcon={ 
        <Badge 
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.5rem',
              fontWeight: '600',
              minWidth: '0',
              height: 'auto',
              padding: '10% 17%',
              borderRadius: '50%'
            },
            width: 'fit-content'
          }}  
          badgeContent={activeBadgeContent} 
          color="primary"
          invisible={activeBadgeContent === 0}
        >
          <FilterList />
        </Badge>
        }
      >
        {/* Hide text on xs, show on sm+ */}
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
          Filters
        </Box>
      </Button>
      }
      <Popover
        open={openFilterForm}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: { 
            width: {xs:'100%',md:'50%'},
            p: '0.5rem 1rem',
            // mt: 1
          }
        }}
      >
        <FormProvider {...filterFormMethod}> 
        <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 500,
              mb: 1
            }}
          >
            Filter Options:
          </Typography>
          {activeBadgeContent > 1 &&<Button 
            size='small' 
            onClick={() => {
              clearFilters();
            }}
            disabled={fields.length === 1}  
            startIcon={<DeleteForever/>}
          >
            Remove all
          </Button>}

        </Box>
          <Box component={'form'} onSubmit={handleSubmit(handleApplyFilter)}>
            {fields.map((field, index) =>{ 
              // check if this filter is from defaultFilterOptions
              const defaultOptions = defaultFilterOptions?.some(
                (opt) =>
                  opt.fieldName === field.fieldName &&
                  opt.operator === field.operator &&
                  opt.value === field.value
              );
              const isDefault = defaultOptions && alwaysKeepDefaultFilterOption;
              return(
              <Grid container spacing={1} key={field.id} sx={{}}>
                {index === 0 ? (
                  <>
                    {fields.length > 1 && <Grid item xs={2}/>}
                    <Grid item xs={3}>
                      <Controller
                        name={`filterOption.${index}.fieldName`}
                        control={control}
                        render={({ field: columnField }) => (
                          <FormControl variant='standard' sx={{ width:'100%' }} size="small">
                            <InputLabel id={`column-select-label-${index}`} sx={{fontSize:'.75em', lineHeight:'2em'}}>
                              Column
                            </InputLabel>
                            <Select
                              {...columnField}
                              labelId={`column-select-label-${index}`}
                              value={columnField.value || ''}
                              disabled={isDefault}
                              // onChange={(e) => {
                              //   // Reset the value and operator when fieldName changes
                              //   setValue(`filterOption.${index}.value`, null);
                              //   setValue(`filterOption.${index}.operator`, 'contains');
                              //   columnField.onChange(e);
                              // }}
                              
                              onChange={(e) => handleColumnChange(e, index, columnField)}
                              sx={{
                                minHeight: '0px',
                                height: '1.6rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                textTransform:'capitalize'
                              }}
                            >
                              {getAvailableColumns(index).map((column) => (
                                <MenuItem key={column.key} value={column.key} sx={{textTransform:'capitalize',fontSize:'0.75rem'}}>
                                  {column.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Controller
                        name={`filterOption.${index}.operator`}
                        control={control}
                        defaultValue='contains'
                        render={({ field: operatorField }) => {
                          const selectedColumn = filterableColumns.find(
                            col => col.key === filterOption[index]?.fieldName
                          );
                          const operators = getOperators(selectedColumn?.type || 'text');
                          console.log(`operators for selected column ${selectedColumn?.type || 'text'}`,operators)

                          return (
                            <FormControl variant='standard' sx={{ width:'100%' }} size="small">
                              <InputLabel id={`operator-select-label-${index}`} sx={{fontSize:'.75em', lineHeight:'2em'}}>
                                Operator
                              </InputLabel>
                              <Select
                                {...operatorField}
                                labelId={`operator-select-label-${index}`}
                                value={operatorField.value || 'contains'}
                                disabled={isDefault}
                                sx={{
                                  minHeight: '0px',
                                  height: '1.6rem',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                }}
                              >
                                {operators.map(op => (
                                  <MenuItem key={op.value} value={op.value} sx={{fontSize:'0.75rem'}}>
                                    {op.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          );
                        }}
                      />
                    </Grid>
                    <Grid item xs={4.5}>
                      <Controller
                        name={`filterOption.${index}.value`}
                        control={control}
                        // disabled={isDefault}
                        render={({ field: valueField }) => renderValueField(valueField, index,isDefault)}
                      />
                    </Grid>
                    <Grid item xs={1} sx={{display:"flex", alignItems:'self-end'}}>
                      {fields.length === 1 && (
                        <IconButton 
                          size='small' 
                          disabled={isDefault}
                          sx={{p:0}}
                          onClick={() => {
                            setValue(`filterOption.${index}.fieldName`, '');
                            setValue(`filterOption.${index}.value`, null);
                            handleClose();
                          }}
                        >
                          <Close/>
                        </IconButton>
                      )}
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={2}>
                      <Controller
                        name={`filterOption.${index}.andOr`}
                        control={control}
                        defaultValue='and'
                        render={({ field: logicField }) => (
                          <FormControl variant='standard' sx={{ width:'100%' }} size="small">
                            <InputLabel id={`logic-select-label-${index}`} sx={{fontSize:'.75em', lineHeight:'2em'}}>
                              Logic
                            </InputLabel>
                            <Select
                              {...logicField}
                              labelId={`logic-select-label-${index}`}
                              value={logicField.value || 'and'}
                              sx={{
                                minHeight: '0px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                height:'1.6rem'
                              }}
                            >
                              <MenuItem value="and" sx={{fontSize:'0.75rem'}}>AND</MenuItem>
                              <MenuItem value="or" sx={{fontSize:'0.75rem'}}>OR</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Controller
                        name={`filterOption.${index}.fieldName`}
                        control={control}
                        render={({ field: columnField }) => (
                          <FormControl variant='standard' sx={{ width:'100%' }} size="small">
                            <InputLabel id={`column-select-label-${index}`} sx={{fontSize:'.75em', lineHeight:'2em'}}>
                              Column
                            </InputLabel>
                            <Select
                              {...columnField}
                              labelId={`column-select-label-${index}`}
                              value={columnField.value || ''}
                              // onChange={(e) => {
                              //   // Reset the value and operator when fieldName changes
                              //   setValue(`filterOption.${index}.value`, null);
                              //   setValue(`filterOption.${index}.operator`, 'contains');
                              //   columnField.onChange(e);
                              // }}
                              onChange={(e) => handleColumnChange(e, index, columnField)}
                              sx={{
                                minHeight: '0px',
                                height: '1.6rem',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                            }}
                            >
                              {getAvailableColumns(index).map((column) => (
                                <MenuItem key={column.key} value={column.key} sx={{textTransform:'capitalize',fontSize:'0.75rem'}}>
                                  {column.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Controller
                        name={`filterOption.${index}.operator`}
                        control={control}
                        defaultValue='contains'
                        render={({ field: operatorField }) => {
                          const selectedColumn = filterableColumns.find(
                            col => col.key === filterOption[index]?.fieldName
                          );
                          const operators = getOperators(selectedColumn?.type || 'text');
                          console.log(`operators for selected column ${selectedColumn?.type || 'text'}`,operators)
                          return (
                            <FormControl variant='standard' sx={{ width:'100%' }} size="small">
                              <InputLabel id={`operator-select-label-${index}`} sx={{fontSize:'.75em', lineHeight:'2em'}}>
                                Operator
                              </InputLabel>
                              <Select
                                {...operatorField}
                                labelId={`operator-select-label-${index}`}
                                value={operatorField.value || 'contains'}
                                sx={{
                                  minHeight: '0px',
                                  height: '1.6rem',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                }}
                              >
                                {operators.map(op => (
                                  <MenuItem key={op.value} value={op.value} sx={{fontSize:'0.75rem'}}>
                                    {op.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          );
                        }}
                      />
                    </Grid>
                    <Grid item xs={4.5}>
                      <Controller
                        name={`filterOption.${index}.value`}
                        control={control}
                        render={({ field: valueField }) => renderValueField(valueField, index)}
                      />
                    </Grid>
                    <Grid item xs={.5} sx={{display:"flex", alignItems:'self-end'}}>
                      <IconButton 
                        size='small' 
                        onClick={() => remove(index)} 
                        sx={{p:0,fontSize:'0.75rem'}}
                        color='error'
                      >
                        <Close sx={{fontSize:'1rem'}} />
                      </IconButton>
                    </Grid>
                  </>
                )}
              </Grid>
            )})}

            <Box sx={{display:'flex', justifyContent:'space-between', mt: 1}}>
              <Button 
                size='small'
                disabled={fields.length === filterableColumns.length} 
                onClick={() => append({ 
                  fieldName: '', 
                  operator: 'contains', 
                  value: '', 
                  andOr: 'and' 
                })} 
                startIcon={<Add/>}
              >
                Add Filter
              </Button>
              <Button 
                size='small' 
                onClick={() => {
                  // reset()
                  handleClose()
                } }
                disabled={activeBadgeContent === 0}  
                // startIcon={<DeleteForever/>}
              >
                Apply Filters
              </Button>
            </Box>
          </Box>
        </FormProvider>
      </Popover>
    </>
  );
};

export default MultiColumnFilter;


// format of filterableColumns
// const filterableColumns = [
//   {
//     key: 'providerName',
//     label: 'Provider Name',
//     type: 'text'
//   },
//   {
//     key: 'bookingNumber',
//     label: 'Booking Number',
//     type: 'number'
//   },
//   {
//     key:'customerName',
//     label:'Customer Name',
//     type:'text'
//   },
//   {
//     key:'add_mobileNumber',
//     label:'Mobile Number',
//     type:'number'
//   },
  // {
  //   key: 'status',
  //   label: 'Status',
  //   type: 'select',
  //   options: [
  //     { value: 'active', label: 'Active' },
  //     { value: 'inactive', label: 'Inactive' }
  //   ]
  // },
  // {
  //   key: 'createdAt',
  //   label: 'Created Date',
  //   type: 'date'
  // },
  // {
  //   key: 'serviceDate',
  //   label: 'Service Date',
  //   type: 'date'
  // },
  // {
  //   key: 'isVerified',
  //   label: 'Is Verified',
  //   type: 'boolean'
  // },
  // {
  //   key: 'reviewStatus',
  //   label: 'Review Status',
  //   type: 'select',
  //   options: [
  //     { value: '1', label: 'Approved' },
  //     { value: '0', label: 'Review Pending' },
  //     { value: '2', label: 'Rejected' }
  //   ]
  // }
// ];

// onApplyFilter function
// const handleApplyFilter = (filterData) => {
//   // Process filter data
//   console.log(filterData);
//   setPaginationModel({
//     ...paginationModel,
//     page: 0, // Reset to first page when applying new filters
//     filters: {
//       ...paginationModel.filters,
//       // Convert MultiColumnFilter options to a format your backend can understand
//     filterOptions: filterData.filterOption.filter(filter => filter.fieldName && filter.value) // Remove empty filters
//     }
//   });
// }